/**
 * Tenant Status Service
 * Handles tenant status validation before and during authentication
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { SuspensionConfig } from '@/types/tenant.types';

export interface TenantStatusResponse {
  tenant_key: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  can_login: boolean;
  message: string;
  suspension_config?: SuspensionConfig | null;
}

export interface TenantAPIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  statusCode?: number;
}

class TenantStatusService {
  /**
   * Check tenant status before login (no auth required)
   * Used by login page to determine if tenant can access the system
   */
  async checkStatus(): Promise<TenantStatusResponse> {
    try {
      const response = await apiClient.get<TenantAPIResponse<TenantStatusResponse>>(
        API_ENDPOINTS.AUTH.TENANT_STATUS
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to check tenant status');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('[TenantStatusService] Error checking tenant status:', error);

      // If API call fails, allow login attempt - backend will handle validation
      // This prevents false positives due to network issues
      return {
        tenant_key: 'unknown',
        status: 'ACTIVE',
        can_login: true,
        message: 'Unable to verify tenant status - proceeding with login',
      };
    }
  }

  /**
   * Get user-friendly error message for tenant status
   */
  getErrorMessage(status: string): string {
    switch (status) {
      case 'INACTIVE':
        return 'This organization account is currently inactive. Please contact your administrator or support for assistance.';
      case 'SUSPENDED':
        return 'This organization account has been suspended. Please contact support to resolve this issue.';
      case 'ACTIVE':
        return 'This organization account is active.';
      default:
        return 'Unable to access this organization at this time. Please contact support.';
    }
  }

  /**
   * Get status badge color
   */
  getStatusColor(status: string): 'green' | 'gray' | 'red' {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'INACTIVE':
        return 'gray';
      case 'SUSPENDED':
        return 'red';
      default:
        return 'gray';
    }
  }

  /**
   * Check if tenant status is active
   */
  isActive(status: string): boolean {
    return status === 'ACTIVE';
  }
}

// Export singleton instance
export const tenantStatusService = new TenantStatusService();
export default tenantStatusService;
